import { ALL_CONSTANTS, ALL_FUNCTIONS } from './formula-editor-config';
import { customMathInstance } from './formula-custom-functions';
import { FormulaEvaluationResult } from '../models/formula-result.model';
import { FORMULA_ERROR_KEYS } from './formula-validation';

/** Set of all known mathjs constant names — used to exclude them from unknown-variable detection */
const ALL_CONSTANTS_SET = new Set(ALL_CONSTANTS);

/** Set of all known mathjs function names — used to exclude them from unknown-variable detection */
const ALL_FUNCTIONS_SET = new Set(ALL_FUNCTIONS);

/**
 * Normalises a formula string before passing it to mathjs.
 *
 * mathjs does **not** support the `&&` / `||` token syntax — it only
 * understands the keyword forms `and` / `or`. This function replaces the
 * C-style logical operators with their mathjs equivalents so that
 * `math.parse()` / `math.evaluate()` do not throw a syntax error.
 *
 * @example
 * normalizeFormulaForMathJs('(a > 0 && b < 10) ? a : b')
 * // → '(a > 0  and  b < 10) ? a : b'
 */
export function normalizeFormulaForMathJs(formula: string): string {
  return formula.replace(/&&/g, ' and ').replace(/\|\|/g, ' or ');
}

/**
 * Extracts identifier tokens from an expression and returns those that are
 * not in the known alias set, not mathjs constants, and not mathjs functions.
 *
 * Mirrors the symbol-extraction logic in `validateFormula`'s AST traversal,
 * using a regex-based approach for evaluation-time pre-checks.
 */
function detectUnknownAliases(expression: string, aliasSet: Set<string>): string[] {
  const tokens = [...new Set(expression.match(/\b[a-zA-Z_]\w*\b/g) ?? [])];
  return tokens.filter(
    (sym) => !aliasSet.has(sym) && !ALL_CONSTANTS_SET.has(sym) && !ALL_FUNCTIONS_SET.has(sym)
  );
}

/**
 * Evaluates a formula expression by substituting variable aliases with their
 * numeric values and computing the result using mathjs.
 *
 * Error handling mirrors validateFormula — uses the same FORMULA_ERROR_KEYS
 * and returns FormulaEvaluationResult which extends FormulaValidationResult.
 *
 * Checks performed in order (first match wins):
 *  1. UNKNOWN_VARIABLES  — identifiers in the expression not present in variableValues
 *                          and not known mathjs constants or functions
 *  2. NULL_VARIABLE_VALUE — aliases in variableValues with null value that appear in the expression
 *  3. EVALUATION_ERROR — mathjs threw a runtime / syntax exception
 *
 * @param expression     - Formula string with variable aliases
 * @param variableValues - Map of alias → numeric value (null = value not yet available)
 */
export function evaluateFormula(expression: string, variableValues: Record<string, number | null>): FormulaEvaluationResult {
  // Build normalised expression (aliases replaced with their string values) for display
  let normalizedExpression = expression;
  // Sort aliases by length descending to avoid partial replacements
  const aliases = Object.keys(variableValues).sort((a, b) => b.length - a.length);
  for (const alias of aliases) {
    const value = variableValues[alias];
    const replacement = value !== null && value !== undefined ? String(value) : 'null';
    normalizedExpression = normalizedExpression.replace(new RegExp(`\\b${alias}\\b`, 'g'), replacement);
  }

  const evalBase: FormulaEvaluationResult = {
    valid: true,
    error: null,
    unknownVariables: [],
    disallowedOperators: [],
    disallowedFunctions: [],
    disallowedConstants: [],
    expression,
    variableValues,
    normalizedExpression,
    result: null
  };

  const aliasSet = new Set(aliases);

  // 1. Detect identifier tokens in the expression that are not known aliases,
  //    mathjs constants, or mathjs functions — these are truly unknown variables.
  const unknownAliases = detectUnknownAliases(expression, aliasSet);
  if (unknownAliases.length > 0) {
    return {
      ...evalBase,
      valid: false,
      unknownVariables: unknownAliases,
      error: `Unknown variable(s): ${unknownAliases.join(', ')}`,
      errorKey: FORMULA_ERROR_KEYS.UNKNOWN_VARIABLES,
      errorParams: { variables: unknownAliases.join(', ') }
    };
  }

  // 2. Detect aliases that appear in the expression but have no numeric value (null).
  //    Without this guard, mathjs would throw "Undefined symbol [alias]".
  const usedNullAliases = aliases.filter(
    (alias) => (variableValues[alias] === null || variableValues[alias] === undefined) && new RegExp(`\\b${alias}\\b`).test(expression)
  );
  if (usedNullAliases.length > 0) {
    return {
      ...evalBase,
      valid: false,
      error: `Variable(s) have no value: ${usedNullAliases.join(', ')}`,
      errorKey: FORMULA_ERROR_KEYS.NULL_VARIABLE_VALUE,
      errorParams: { variables: usedNullAliases.join(', ') }
    };
  }

  // 3. Evaluate — catch any runtime / syntax exceptions.
  try {
    const scope: Record<string, number> = {};
    for (const alias of aliases) {
      const value = variableValues[alias];
      if (value !== null && value !== undefined) {
        scope[alias] = value;
      }
    }
    const result = customMathInstance.evaluate(expression, scope);
    const numericResult = typeof result === 'number' ? result : typeof result === 'boolean' ? (result ? 1 : 0) : null;
    return { ...evalBase, result: numericResult };
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Evaluation error';
    return {
      ...evalBase,
      valid: false,
      error: message,
      errorKey: FORMULA_ERROR_KEYS.EVALUATION_ERROR,
      errorParams: { message }
    };
  }
}
